import React, { useEffect, useState } from "react";
import GameBackground from "../../../assets/images/game-star.png";
import "../../../assets/css/index.css";
import useGameBubblePop from "../hooks/useGameBubblePop";
import { emptyBubble, isAmazonCampaign, isNativeWebview } from "../../../common/constants";
import InstructionalModal from "./instructionModal";
import { generateCloudinaryImageUrl, getLocalStorage } from "../../../utils/common-function";
import WaitlistModal from "../../../components/waitlistModal";
import { formatAmountToCurrency, formatTimeInSeconds } from "../../../utils/format";
import clsx from "clsx";
import { Stashlink } from "../../../types";
import BrandHeader from "../../../components/BrandHeader";
import Clock from "../../../assets/images/clock.svg";
import plantEndScreen from "../../../assets/images/plant-end-screen.png";
import { TreeIcon } from "./trees";

const BubblePopGame: React.FC = () => {
  const {
    bubbles,
    setTotalBubbles,
    total,
    isGameComplete,
    focusCustomBackground,
    isModalOpen,
    revealBubble,
    time,
    closeModal,
    isServerError,
    closeWaitListModal,
    setIsModalOpen
  } = useGameBubblePop();
  const [showCoin, setShowCoin] = useState(false);
  const [key, setKey] = useState(0);
  const brandsData = getLocalStorage("brandsData");
  const stashlink = getLocalStorage("stashlink") as Stashlink;
  const campaign = stashlink?.campaign;

  if (isServerError) {
    return <WaitlistModal isOpen={isServerError} onClose={closeWaitListModal} />;
  }

  const hasCustomBackground = !!stashlink?.promoBrandingImageUrl;
  const shouldHideTimer = isGameComplete || focusCustomBackground;
  const isAmazon = isAmazonCampaign(stashlink?.id);

  useEffect(() => {
    if (showCoin) {
      const timer = setTimeout(() => {
        setShowCoin(false);
      }, 400); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [key]);

  useEffect(() => {
    if (isAmazonCampaign(stashlink?.id)) {
      setIsModalOpen(true);
    } else {
      setTimeout(() => {
        closeModal();
      }, 500);
    }
  }, []);

  const handleCoinClick = () => {
    // If coin is already showing, reset it
    if (showCoin) {
      setShowCoin(false);
      // Small delay to ensure state updates before showing again
      setTimeout(() => {
        setKey((prev) => prev + 1);
        setShowCoin(true);
      }, 10);
    } else {
      setShowCoin(true);
    }
  };

  function getImageIndex(value: string) {
    return Math.min(Math.ceil((Number(value) * 100) / 10), 10);
  }

  return (
    <div className="flex flex-col items-center bg-soft-black text-white select-none flex-1">
      <div className={clsx(["w-full relative z-[100] flex flex-row items-center justify-between"])}>
        <BrandHeader brand={brandsData?.sampleEligibleBrands?.brand} isReady />
      </div>
      {hasCustomBackground ? (
        <div className="absolute h-screen inset-0 flex items-center justify-center z-10">
          <div
            className={clsx([
              "transition-all duration-[1000ms] absolute size-full bg-black z-10",
              focusCustomBackground ? "opacity-0" : "opacity-65"
            ])}
          />
          <img
            src={generateCloudinaryImageUrl(stashlink?.promoBrandingImageUrl || "", "585", "1266")}
            alt=""
            className={clsx([
              "w-full h-screen object-cover disable-interaction transition-all duration-[1000ms]",
              isGameComplete ? "scale-0" : "scale-100"
            ])}
          />
        </div>
      ) : null}
      <div className="w-full h-full xxsh:h-c-78 flex flex-col items-center justify-between relative overflow-hidden">
        {/* <div className="flex items-center justify-between px-4 w-full relative z-20 mt-3">
          <div
            className={`${total === 0 ? "text-grey" : "text-base-white"} lining-nums tabular-nums base-white px-4 py-2 bg-grey-bg rounded-full backdrop-blur-2 min-w-28 flex items-center justify-center font-semibold text-base
             leading-tight ${shouldHideTimer ? "hide-timer" : ""}`}>
            {formatTimeInSeconds(time) + "s"}
          </div>
          <div
            className={`${total === 0 ? "bg-grey-bg" : "bg-[#d7ff3e]"} ${shouldHideTimer ? "hide-amount" : ""} h-9  min-w-32 font-primary rounded-4xl flex text-19 flex-col items-center justify-center font-extrabold capitalize lining-nums proportional-nums`}>
            <div className={`leading-tight ${total === 0 ? "text-grey" : "text-black"}`}>${total === 0 ? total : total.toFixed(2)}</div>
          </div>
        </div> */}
        <div
          id="game"
          className={clsx([
            "transition-all duration-[1000ms] w-full relative flex flex-grow  justify-center px-4",
            isNativeWebview() ? "items-start pt-16" : "items-center pt-0"
          ])}>
          <div
            className={clsx([
              "absolute inset-0 flex items-center justify-center z-10 transition-all duration-[1000ms]",
              !hasCustomBackground && !isGameComplete ? "block" : "hidden"
            ])}>
            <img src={GameBackground} alt="" className="bubble-game-background w-full object-contain disable-interaction" />
          </div>

          <div className="w-full flex flex-col z-30 scale-[85%] xs:scale-100">
            <div className="w-full flex justify-between items-center z-30 mb-4 h-12">
              {total === 0 ? (
                <div className="flex flex-col items-center justify-center mx-auto">
                  {!isAmazon && <div className="text-20px font-black text-shadow-game">START</div>}
                  <div className="flex flex-row items-center justify-center gap-x-1 text-base text-white leading-tight-18.2 w-full text-center font-bold text-shadow-game">
                    <div className="text-[34px] animate-bounce">👇</div>{" "}
                    {isAmazon ? (
                      <>
                        <span>Tap coins to unlock a tree!</span>
                      </>
                    ) : (
                      <>
                        Every coin tapped unlocks
                        <br /> donations for charity
                      </>
                    )}
                    <div className="text-[34px] animate-bounce">👇</div>
                  </div>
                </div>
              ) : (
                <div
                  className={`text-white flex flex-row items-center justify-center gap-x-1 w-fit lining-nums tabular-nums base-white px-4 py-2 bg-grey-bg rounded-full backdrop-blur-2 font-semibold text-sm
                    leading-snug ${shouldHideTimer ? "hide-timer" : ""}`}>
                  <img src={Clock} alt="clock" className="size-6" /> {formatTimeInSeconds(time) + "s"}
                </div>
              )}
              {total !== 0 &&
                (isAmazon ? (
                  <div className="relative">
                    <div
                      className={clsx(
                        "z-10",
                        "text-base font-extrabold flex flex-col items-center justify-center font-primary lining-nums tabular-nums bg-[#364110] w-14 h-9  relative",
                        "text-primary-l1",
                        "bg-[#0d4a129e]",
                        "rounded-full",
                        shouldHideTimer ? "hidden" : ""
                      )}>
                      <div className="flex items-center justify-center w-14 h-9 -mt-10">
                        <div className="mb-8">
                          <TreeIcon step={getImageIndex(String(total))} />
                        </div>
                      </div>
                    </div>
                    {showCoin && <div key={key} className="absolute -z-20 left-2 bottom-5 coin coin-bounce"></div>}
                  </div>
                ) : (
                  <div className="relative">
                    <div
                      className={clsx(
                        "z-10",
                        "text-base font-extrabold flex flex-col items-center gap-y-0 justify-center font-primary lining-nums tabular-nums bg-grey-bg py-1 px-6 rounded-full text-grey",
                        "text-primary-l1",
                        "bg-[#374019]",
                        shouldHideTimer ? "hidden" : ""
                      )}>
                      {formatAmountToCurrency(total, navigator.language, 2)}
                      <div className="-mt-1 text-[#d7ff3e] text-[10px] font-medium font-secondary leading-[14px] tracking-wide">
                        Donated
                      </div>
                    </div>
                    {showCoin && <div key={key} className="absolute -z-20 left-1/3 bottom-5 coin coin-bounce"></div>}
                  </div>
                ))}
            </div>
            <div
              className={`relative z-30 grid grid-cols-7 gap-2 mb-1 game-bubble-transform ${focusCustomBackground || isGameComplete ? "game-bubble-resize" : ""}`}>
              {bubbles.map((bubble, index) =>
                emptyBubble.includes(index) ? (
                  <div key={index} className="w-10 h-10 rounded-full"></div>
                ) : (
                  <div
                    key={index}
                    onClick={() => {
                      revealBubble(index, handleCoinClick);
                    }}
                    onTouchStart={() => {
                      revealBubble(index, handleCoinClick);
                    }}
                    className={`${bubble.revealed ? "flipping revealed-coin" : "coin"}`}>
                    {bubble.revealed && (
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer ${
                          bubble.revealed ? "bg-gray-500/30" : "bg-primary-l1/40"
                        }`}></div>
                    )}
                  </div>
                )
              )}
            </div>
          </div>

          {isAmazon ? (
            <div
              className={clsx([
                "absolute flex flex-col gap-3 pointer-events-none z-30 transition-all duration-[1000ms] w-[300px]",
                !hasCustomBackground && !isGameComplete && "hidden",
                hasCustomBackground && !focusCustomBackground && "hidden",
                hasCustomBackground && focusCustomBackground && !isGameComplete && "hidden",
                hasCustomBackground && focusCustomBackground && isGameComplete && "block h-full !transform-none",
                isNativeWebview() ? "items-start justify-start  self-start" : "items-center justify-center  self-center"
              ])}>
              <div
                className={clsx([
                  "w-[556px] h-[556px] left-1/2 -translate-x-1/2 -z-10 absolute -top-16 inset-0 flex items-center justify-center transition-all duration-[1000ms]",
                  hasCustomBackground && !isGameComplete && "hidden"
                ])}>
                <img src={GameBackground} alt="" className="bubble-game-background w-full object-contain disable-interaction" />
              </div>

              <div
                className={`flex flex-col relative rounded-3xl bg-[#1f1f23]/70 z-20 items-center backdrop-blur-[2px] amountEarn ${isNativeWebview() ? "top-32 justify-start mt-0" : "!translate-y-0 justify-center mt-16"} ${
                  isGameComplete ? "" : "initial-position"
                } ${isGameComplete ? "amountEarn-transformation" : ""}`}>
                <img
                  src={plantEndScreen}
                  alt="plant-end-screen"
                  className="object-contain disable-interaction z-20 absolute -top-[110px]"
                  height={126}
                  width={204}
                />
                <img src="/campaign/game-complete-title.png" alt="header" className="absolute -top-[24px]" />
                <div className="w-[283px] p-8 border-background-1 border-2 rounded-[40px] h-[336px] flex items-center flex-col bg-[#353d17]">
                  <img src="/campaign/game-complete-text.png" alt="text" className="mt-[48px]" />
                  <button
                    className="border-3 border-background-1 mt-[40px] w-[188px] mx-auto rounded-full flex flex-col py-1.5"
                    style={{ background: "linear-gradient(360deg, #D7FF40 0%, #E8FF8C 99.41%)", boxShadow: "0px 8px 0px 0px #000000" }}>
                    <span className="font-primary font-extrabold text-black text-28px block leading-[105%] w-full text-center">
                      {formatTimeInSeconds(time)}
                    </span>
                    <span className="text-14px text-black block leading-[105%] font-extrabold font-primary w-full text-center">
                      seconds
                    </span>
                  </button>
                </div>

                {/* <img
                  src={amazonBackground}
                  alt="amazonBackground"
                  className="amazon-background w-full object-contain disable-interaction"
                /> */}

                {/* <div className="absolute flex flex-col mt-9 text-center justify-center">
                  <div className="text-center justify-center items-center font-sans font-bold text-[40px] leading-[87%] break-words text-neutral-50 flex pb-6 gap-2.5">
                    <span>In just</span>
                    <div>
                      <span className="border-[1px] border-primary-l1 rounded-full font-sans font-extrabold text-19 text-primary-l1 px-2">
                        {" "}
                        {formatTimeInSeconds(time)}s{" "}
                      </span>
                    </div>
                  </div>
                </div> */}
              </div>
            </div>
          ) : (
            <div
              className={clsx([
                "absolute pt-20 flex flex-col gap-3 items-center justify-center pb-6 top- pointer-events-none z-30 transition-all duration-[1000ms]",
                !hasCustomBackground && !isGameComplete && "hidden",
                hasCustomBackground && !focusCustomBackground && "hidden",
                hasCustomBackground && focusCustomBackground && !isGameComplete && "hidden",
                hasCustomBackground && focusCustomBackground && isGameComplete && "block"
              ])}>
              <div
                className={clsx([
                  "w-[556px] h-[556px] left-1/2 -translate-x-1/2 -z-10 absolute -top-16 inset-0 flex items-center justify-center transition-all duration-[1000ms]",
                  hasCustomBackground && !isGameComplete && "hidden"
                ])}>
                <img src={GameBackground} alt="" className="bubble-game-background w-full object-contain disable-interaction" />
              </div>
              {/* Added the Dollar amount earn for game completion */}
              <div
                className={`flex flex-col rounded-3xl min-w-[85%] py-4 px-8 bg-[#1f1f23]/70 justify-start z-20 items-center backdrop-blur-[2px] amountEarn ${
                  isGameComplete ? "final-position" : "initial-position"
                } ${isGameComplete ? "amountEarn-transformation" : ""}`}>
                <span className="text-center pb-3 self-stretch font-secondary font-bold text-lg break-words font-wght-740 leading-[130%] text-neutral-50 inline text-shadow-game">
                  {" "}
                  In just {formatTimeInSeconds(time)}s
                </span>
                <div
                  className={`mb-3 py-2 px-4 bg-[#d7ff3e] rounded-[999px] text-center text-black text-32px font-extrabold font-primary flex justify-center items-center capitalize leading-[33.76px] ${
                    isGameComplete ? "amountEarn-transformation" : ""
                  }`}>
                  {formatAmountToCurrency(1, navigator.language, 2)}
                </div>
                <div className="text-center">
                  <span className="self-stretch justify-center font-secondary text-sm text-white leading-[21px] font-medium inline text-shadow-game">
                    was donated to
                  </span>
                  <br />
                  <span className="self-stretch font-secondary font-bold text-lg break-words font-wght-740 leading-[130%] text-neutral-50 inline text-shadow-game">
                    {" "}
                    {campaign?.title}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* <p className="font-secondary mt-5 text-base text-white leading-tight font-normal">Every cent you stash is donated by </p>
      <h1 className="font-primary capitalize text-center text-28 font-extrabold mt-2 leading-tight-29.54 text-neutral-50 mx-1">
        {brandsData?.sampleEligibleBrands?.brand.name || ""}
      </h1> */}
      <InstructionalModal
        isOpen={isModalOpen}
        onClose={closeModal}
        brandName={brandsData?.sampleEligibleBrands?.brand.name || ""}
        bubbles={bubbles}
        revealBubble={revealBubble}
        setTotalBubbles={setTotalBubbles}
        focusCustomBackground={focusCustomBackground}
        isGameComplete={isGameComplete}
      />
    </div>
  );
};

export default BubblePopGame;
